import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { ActionButtons, type ActionItem } from "@/admin/ActionItem.tsx";
import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { EnumComponentFilter, NumberRangeFilter, TextFilter, UnixDateRangeFilter } from "@/admin/filters.tsx";
import { unassignAction } from "@/admin/floating_ips/unassignAction.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import { OrderDirection, VmOrderFields, type GetVmListQuery } from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { useCursorPager } from "@/admin/pager.ts";
import {
    baField,
    baIdField,
    createdAtField,
    currencyField,
    hostPoolField,
    locationField,
    numberField,
    userIdField,
} from "@/admin/table_fields/standardFields.tsx";
import { DeleteVmBlockModalContent } from "@/admin/vm/DeleteVmBlock.tsx";
import { MigrateVmBlockModalContent } from "@/admin/vm/MigrateVmBlock.tsx";
import { NicIftuneButton } from "@/admin/vm/NicIftuneBlock.tsx";
import { StartVmBlockModalContent } from "@/admin/vm/StartVmBlock.tsx";
import { StopVmBlockModalContent } from "@/admin/vm/StopVmBlock.tsx";
import { VmDetailView } from "@/admin/vm/VmDetailView.tsx";
import { VmOs } from "@/admin/vm/VmOs.tsx";
import { VmToolbar } from "@/admin/vm/VmToolbar.tsx";
import { DISABLED } from "@/components/Action.tsx";
import { showModal } from "@/components/modal/registerModal.tsx";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import type { SelectedOs } from "@/modules/compute/os/os.ts";
import { vmStatuses } from "@/modules/compute/statusLabels.tsx";
import { imagesByOsNameAtom } from "@/modules/compute/vmImagesQuery.ts";
import { VmStatusBadge } from "@/modules/compute/VmStatusBadge.tsx";
import { queryAtom } from "@/modules/location/locationQuery.ts";
import { PreloadAtom } from "@/utils/jotai/PreloadAtom.tsx";
import { useQuery } from "@apollo/client/react";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { VmStatusEnum } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { atom } from "jotai/vanilla";

// eslint-disable-next-line react-refresh/only-export-components
export const GET_VMS = gql(/* GraphQL */ `
    query GetVmList(
        $limit: Int
        $orderField: VmOrderFields
        $orderDir: OrderDirection
        $cursor: String
        $page: Int
        $locations: [String!]
        $search: String
        $filters: [VmFilter!]
    ) {
        admin_vm_list(
            limit: $limit
            orderField: $orderField
            orderDir: $orderDir
            cursor: $cursor
            page: $page
            locations: $locations
            search: $search
            filters: $filters
        ) {
            items {
                uuid
                service_uuid
                hypervisor_uuid
                # hypervisor_name
                username
                updated_at

                is_designated_pool_visible
                designated_pool_name
                designated_pool_id
                designated_pool_uuid

                is_current_pool_visible
                current_pool_name

                billing_account
                billing_account_object {
                    ...BillingAccountBlock
                    running_totals {
                        ongoing
                    }
                }

                name
                description
                memory
                vcpu
                nic_iftune {
                    average_kbps
                    peak_kbps
                    burst_kb
                }
                status
                mac
                public_ipv6
                private_ipv4
                ip_address_object {
                    address
                    _location
                }
                os_name
                os_version
                user_id
                created_at
                deleted_at
                _location
                boot_firmware
                storage {
                    created_at
                    name
                    pool
                    primary
                    shared
                    size
                    type
                    user_id
                    uuid
                }
            }
            paging {
                total
                cursor
            }
        }
    }
`);

export type GQVmItem = NonNullable<GetVmListQuery["admin_vm_list"]["items"]>[number];

const vmFields: GqlFieldsOf<GQVmItem, VmOrderFields> = [
    gf({
        id: "uuid",
        title: "UUID",
        get: (a) => a.uuid,
        copyable: true,
        order: VmOrderFields.Uuid,
        filter: TextFilter,
    }),
    gf({
        ...userIdField,
        get: (a) => a.user_id,
        order: VmOrderFields.UserId,
    }),

    gf({
        ...baIdField,
        get: (a) => a.billing_account,
        order: VmOrderFields.BillingAccount,
    }),
    gf({
        ...baField,
        get: (a) => a.billing_account_object,
        render: BillingAccountBlock,
    }),
    gf({
        id: "service_uuid",
        title: "Service UUID",
        get: (a) => a.service_uuid,
        copyable: true,
        order: VmOrderFields.ServiceUuid,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "hypervisor_uuid",
        title: "Host UUID",
        get: (a) => a.hypervisor_uuid,
        copyable: true,
        order: VmOrderFields.HostUuid,
        // XXX: Filtering/sorting by this field is broken for some reason
        // order: VmOrderFields.HypervisorUuid,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        ...currencyField,
        id: "ongoing_balance",
        title: "Ongoing\nBalance",
        get: (a) => a.billing_account_object?.running_totals?.ongoing ?? 0,
        render: OngoingBalance,
    }),
    gf({
        ...createdAtField,
        get: (a) => (a.created_at ? apiUnixToDate(a.created_at as number) : undefined),
        order: VmOrderFields.CreatedAt,
    }),
    gf({
        id: "deleted_at",
        title: "Deleted At",
        get: (a) => (a.deleted_at ? apiUnixToDate(a.deleted_at as number) : undefined),
        order: VmOrderFields.DeletedAt,
        filter: UnixDateRangeFilter,
        hidden: true,
    }),
    gf({
        id: "status",
        title: "Status",
        get: (a) => a.status as VmStatusEnum,
        order: VmOrderFields.Status,
        render: VmStatusBadge,
        filter: () => <EnumComponentFilter mode="csv" values={vmStatuses} component={VmStatusBadge} />,
    }),
    gf({
        id: "os_name",
        title: "OS",
        get: (a): SelectedOs => ({ os_name: a.os_name!, os_version: a.os_version! }),
        render: VmOs,
    }),
    gf({
        id: "name",
        title: "VM Name",
        get: (a) => a.name,
        order: VmOrderFields.DisplayName,
        filter: TextFilter,
    }),
    gf({
        id: "public_ipv6",
        title: "Public IPv6",
        get: (a) => a.public_ipv6,
        copyable: true,
        order: VmOrderFields.PublicIpv6,
        filter: TextFilter,
    }),
    gf({
        id: "address",
        title: "Public IPv4",
        get: (a) => a.ip_address_object?.address,
        copyable: true,
        filter: TextFilter,
    }),
    gf({
        id: "private_ipv4",
        title: "Private IPv4",
        get: (a) => a.private_ipv4,
        copyable: true,
        order: VmOrderFields.PrivateIpv4,
        filter: TextFilter,
    }),
    gf({
        id: "mac",
        title: "MAC Address",
        get: (a) => a.mac,
        copyable: true,
        order: VmOrderFields.Mac,
        filter: TextFilter,
    }),
    gf({
        id: "limitations",
        title: "Bandwidth\nLimitations",
        get: (a) => a,
        render: NicIftuneButton,
    }),

    gf({
        ...numberField,
        id: "vcpu",
        title: "CPUs",
        get: (a) => a.vcpu,
        order: VmOrderFields.Vcpu,
        filter: NumberRangeFilter,
    }),
    gf({
        ...numberField,
        id: "memory",
        title: "RAM\n(MB)",
        get: (a) => a.memory,
        order: VmOrderFields.Memory,
        filter: NumberRangeFilter,
    }),
    gf({
        ...numberField,
        id: "disk",
        title: "Boot\nDisk (GB)",
        get: (a) => a.storage?.[0]?.size,
    }),
    gf({
        id: "username",
        title: "Username",
        get: (a) => a.username,
        order: VmOrderFields.Username,
        filter: TextFilter,
    }),
    gf({
        ...hostPoolField,
        order: VmOrderFields.DesignatedPoolUuid,
        get: (a) => a.designated_pool_uuid,
    }),
    gf({
        ...locationField,
        get: (a) => a._location,
    }),
];

function vmActions(item: GQVmItem): ActionItem[] {
    const isRunning = item.status === "running";
    const isStopped = item.status === "stopped";
    const isDeleted = item.status === "deleted";

    return [
        {
            id: "start_vm",
            title: "Start",
            icon: "jp-icon-run",
            action: isRunning || isDeleted ? DISABLED : () => showModal(<StartVmBlockModalContent item={item} />),
        },
        {
            id: "stop_vm",
            title: "Stop",
            icon: "jp-icon-stop",
            action: isStopped || isDeleted ? DISABLED : () => showModal(<StopVmBlockModalContent item={item} />),
        },
        {
            id: "force_stop_vm",
            title: "Force Stop",
            icon: "jp-icon-stop",
            action: isStopped || isDeleted ? DISABLED : () => showModal(<StopVmBlockModalContent item={item} force />),
        },
        {
            id: "migrate_vm",
            title: "Migrate",
            icon: "jp-export-icon",
            action: isDeleted ? DISABLED : () => showModal(<MigrateVmBlockModalContent item={item} />),
        },
        unassignAction(
            item.ip_address_object
                ? { address: item.ip_address_object.address, location: notNull(item._location) }
                : null,
        ),
        {
            id: "delete_vm",
            title: "Delete",
            icon: "jp-trash-icon",
            action: isDeleted ? DISABLED : () => showModal(<DeleteVmBlockModalContent item={item} />),
        },
    ];
}

const prefetchAtom = atom((get) => {
    // Preload for the `VmOs` component for the "OS" field
    get(imagesByOsNameAtom);
    get(queryAtom);
});

export function VmsTable() {
    const table = (
        <GraphqlTable
            title="Virtual Machines"
            fields={vmFields}
            defaults={{ orderField: VmOrderFields.CreatedAt, orderDir: OrderDirection.Desc }}
            getId={(item) => item.uuid}
            usePager={useCursorPager}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_VMS, { variables });
                return extractData(q, (d) => d.admin_vm_list);
            }}
            actionButtons={(item) => <ActionButtons actions={vmActions(item)} inTable />}
            renderDetailToolbar={(item) => <VmToolbar vmUuid={item.uuid} location={notNull(item._location)} />}
            renderDetail={(item) => <VmDetailView vmUuid={item.uuid} location={notNull(item._location)} />}
        />
    );

    return (
        <>
            <PreloadAtom atom={prefetchAtom} />
            {table}
        </>
    );
}
